"use client";
import { useEffect, useState } from "react";
import { useRef } from "react";
import moment from "moment";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";
import { Spinner } from "react-bootstrap";

import tippy, { followCursor, Instance, Props } from "tippy.js";
import "tippy.js/dist/tippy.css";
import toast, { Toaster } from "react-hot-toast";

import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../Calendar/Calendar.css";
import { cal_record, cal_place } from "../../class/prisma/db";

import CalendarUtil from "./utils/CalendarUtil";
import CalendarBackend from "./utils/CalendarBackend";

import EventModal from "../Modal/EventModal";
import DeleteEventModal from "..//Modal/DeleteEventModal";
import { ButtonDropdownMenuOption } from "..//ButtonDropDownMenu/ButtonDropDownMenu";
import CalendarEventRender from "..//Calendar/utils/CalendarEventRender";
import { useDispatch, useSelector } from "react-redux";
import {
  setEvents,
  setHolidays,
  setCalendars,
  setPlaces,
} from "../../store/features/cacheSlice";
import common from "../../utils/common";
import { RootState } from "../../store/store";
import { CalendarData, calendarDataSchema } from "../../class/frontend.schema";
import { AxiosResponse } from "axios";
import {
  CalendarApi,
  DateSelectArg,
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventHoveringArg,
} from "@fullcalendar/core/index.js";
import { SystemMsg } from "../../utils/SystemMsg";
import IconButtonGroup, { CheckIcon } from "../../components/IconButtonGroup";
import CalendarListModal from "../../components/Modal/CalendarListModal";
import PlaceListModal from "../../components/Modal/PlaceListModal";
import { CalendarViewObject } from "../../class/calendar.schema";
import { useI18n } from "../../i18n";

export default function Calendar({
  container_offsetLeft,
}: {
  container_offsetLeft: number;
}) {
  const { locale } = useI18n();

  const CALENDAR_LOCALE_MAP = {
    en: "en",
    "zh-Hant": "zh-tw",
    "zh-Hans": "zh-cn",
  };

  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);
  const calendarRef = useRef<FullCalendar | null>(null);

  const [calendarEventArray, setCalendarEventArray] = useState<any[]>([]);

  const [showEventModal, setShowEventModal] = useState<boolean>(false);
  const [eventModalMode, setEventModalMode] = useState<string>("add");
  const [dayInfo, setDayInfo] = useState<
    DateSelectArg | EventClickArg | EventContentArg
  >();
  const [currentCalDate, setCurrentCalDate] = useState<Date>(moment().toDate());
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedCalendars, setSelectedCalendars] = useState<number[]>([]);
  const [calendarOptions, setCalendarOptions] = useState<
    ButtonDropdownMenuOption[]
  >([]);

  const [showPlaceModal, setShowPlaceModal] = useState<boolean>(false);
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [selectedCount, setSelectedCount] = useState<number>(0);

  const holidays = useSelector((state: RootState) => state.cache.holidays);
  const events = useSelector((state: RootState) => state.cache.events);
  const places = useSelector((state: RootState) => state.cache.places);
  const calendars = useSelector((state: RootState) => state.cache.calendars);

  useEffect(() => {
    CalendarBackend.getHolidayData(currentCalDate, (data) => {
      if (!Array.isArray(data)) {
        return;
      }
      const oldData =
        holidays && Array.isArray(holidays) && (holidays as any[]).length > 0
          ? (holidays as any[])
          : [];
      const tempData = [...oldData];
      for (const item of data) {
        const exists = oldData.find(
          (x) => x.id.toString() === item.id.toString(),
        );
        if (!exists) {
          tempData.push(item);
        }
      }
      dispatch(setHolidays(tempData));
    });
    CalendarBackend.getRecordData(currentCalDate, (data) => {
      if (Array.isArray(data)) {
        const oldData =
          events && Array.isArray(events) && (events as any[]).length > 0
            ? (events as any[])
            : [];
        const tempData = [...oldData];
        for (const item of data) {
          const exists = oldData.find(
            (x) => x.id.toString() === item.id.toString(),
          );
          if (!exists) {
            tempData.push(item);
          }
        }
        dispatch(setEvents(tempData));
      }
    });
  }, [currentCalDate]);

  useEffect(() => {
    console.log({
      events,
      holidays,
      calendars,
    });
    if (
      !Array.isArray(events) &&
      !Array.isArray(holidays) &&
      !Array.isArray(calendars)
    ) {
      return;
    }
    let tempArray: CalendarData[] = [];
    if (Array.isArray(events)) {
      tempArray = tempArray.concat(events);
    }
    if (Array.isArray(holidays)) {
      const map = new Map([
        ["en", "title_en"],
        ["zh-Hant", "title_zh"],
        ["zh-Hans", "title_cn"],
      ]);
      const key = map.get(locale) ?? "title_en";
      const translatedHoliday = (holidays as Array<any>)?.map((entry) => {
        return {
          ...entry,
          title: entry[key] ?? entry.name,
        };
      });
      tempArray = tempArray.concat(translatedHoliday);
    }

    setCalendarEventArray([...tempArray]);
    setLoaded(true);
  }, [events, holidays, calendars, locale]);

  useEffect(() => {
    if (!Array.isArray(calendars)) {
      CalendarBackend.getCalendarData((data) => {
        if (Array.isArray(data)) {
          dispatch(setCalendars(data));
        }
      });
    } else {
      const options: ButtonDropdownMenuOption[] = [];
      const calendarObjects = calendars as unknown as CalendarViewObject[];
      for (const item of calendarObjects) {
        options.push({
          value: item.id,
          label: item.name,
          color: item.color ?? "",
          checkIcon: <CheckIcon backgroundColor={item.color ?? "#d3d3d3"} />,
        });
      }
      setSelectedCalendars(
        calendarObjects
          .filter((x) => x.id > 0 && x.defaultShow === true)
          .map((x) => x.id),
      );

      setCalendarOptions(options);
    }
  }, [calendars]);

  useEffect(() => {
    if (!Array.isArray(places)) {
      CalendarBackend.getPlaceData((data) => {
        if (Array.isArray(data)) {
          dispatch(setPlaces(data));
        }
      });
    }
  }, [places]);

  useEffect(() => {
    const calendarApi = calendarRef?.current?.getApi();
    if (!calendarApi) {
      return;
    }
    calendarRef.current?.render();
  }, [selectedCalendars]);

  //#region Event Modal
  const onHideEventModal = () => {
    setShowEventModal(false);
    setTimeout(() => {
      const calendarApi = calendarRef?.current?.getApi();
      calendarApi?.unselect();
      console.log(203, `unselected...`);
    }, 200);
  };

  const onSaveBtnClick = (
    resp: AxiosResponse,
    calendarApi?: CalendarApi,
    mode?: string,
    data?: CalendarData | cal_record,
  ) => {
    if (!calendarApi || !data || !eventModalMode) {
      return;
    }
    const isAddMode = mode === "add";
    const isEditMode = mode === "edit";
    const isSuccess =
      resp?.status === 200
        ? mode === "add" && Array.isArray(resp?.data)
          ? true
          : mode === "edit"
        : false;
    if (isAddMode) {
      const transformedData = CalendarBackend.transformRecordData(resp?.data);
      addEntryAction(isSuccess, transformedData, calendarApi);
    } else if (isEditMode) {
      editEntryAction(isSuccess, data as CalendarData, calendarApi);
    }
  };

  const addEntryAction = (
    success: boolean,
    response_data: any,
    calendarApi: CalendarApi,
  ) => {
    if (!success) {
      toast.error(SystemMsg.Record.createFailed);
      return;
    }
    for (const record of response_data) {
      const tempData = { ...record, id: record.id.toString() };
      const parseResult = calendarDataSchema.safeParse(tempData);
      if (!parseResult.success) {
        continue;
      }
      calendarEventArray.push(parseResult.data);
      setCalendarEventArray([...calendarEventArray]);
      calendarApi.addEvent(parseResult.data);
    }

    const singleAdd =
      Array.isArray(response_data) && response_data.length === 1;
    const num = singleAdd ? response_data[0].id : response_data.length;
    toast.success(
      singleAdd
        ? SystemMsg.Record.createOneSuccess(num)
        : SystemMsg.Record.createManySuccess(num),
    );
  };

  const editEntryAction = (
    success: boolean,
    entry_data: CalendarData,
    calendarApi: CalendarApi,
  ) => {
    if (!success) {
      toast.error(SystemMsg.Record.updateFailed(Number(entry_data.id)));
      return;
    }
    //UI Event
    const calEvent = calendarApi.getEventById(entry_data.id);
    if (!calEvent) {
      return;
    }
    calEvent.setProp("title", entry_data.title);
    calEvent.setExtendedProp("description", entry_data.description);
    calEvent.setExtendedProp("tag", entry_data.tag);
    calEvent.setStart(entry_data.start);
    calEvent.setEnd(entry_data.end);
    if (
      entry_data?.placeId &&
      common.strToNumber(entry_data?.placeId?.toString()) > 0
    ) {
      calEvent.setExtendedProp("placeId", entry_data.placeId);
    }
    calEvent.setExtendedProp("calendarIds", entry_data.calendarIds);

    //Event Array
    const arr = [];
    for (const evt of calendarEventArray) {
      if (evt.id.toString() !== entry_data.id) {
        arr.push(evt);
        continue;
      }
      arr.push(entry_data);
    }
    setCalendarEventArray(arr);
    toast.success(SystemMsg.Record.updateSuccess(Number(entry_data.id)));
  };
  //#endregion

  //#region Delete Modal
  const onHideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const onDeleteBtnClick = (
    resp: AxiosResponse,
    calendarApi: CalendarApi,
    id?: number,
  ) => {
    if (!calendarApi) {
      toast.error(SystemMsg.CalendarUi.failedToGetCalendarApi);
      return;
    }
    if (id === undefined || id === null || id === -1) {
      toast.error(SystemMsg.Record.recordIdInvalid);
      return;
    }
    const isSuccess = resp?.status === 200;
    if (!isSuccess) {
      toast.error(SystemMsg.Record.deleteFailed(id));
      return;
    }

    const calEvent = calendarApi.getEventById(id.toString());
    if (!calEvent) {
      toast.error(SystemMsg.CalendarUi.failedToFindEvent(id));
      return;
    }
    calEvent.remove();
    setCalendarEventArray(
      calendarEventArray.filter((x) => x.id.toString() !== id.toString()),
    );
    toast.success(SystemMsg.Record.deleteSuccess(id));
  };
  //#endregion

  const datesSet = (dateInfo: DatesSetArg) => {
    //On Month Change
    const loaded = Array.isArray(events) && Array.isArray(holidays);
    if (dateInfo && loaded) {
      setCurrentCalDate(moment(dateInfo.start).toDate());
    }
  };

  let tippyInstance: Instance<Props>[];
  const eventMouseEnter = (mouseEnterInfo: EventHoveringArg) => {
    const title = mouseEnterInfo?.event?.title ?? "";
    const start = mouseEnterInfo?.event?.start;

    if (title.length === 0 || !start) {
      return;
    }

    let contentHTML = "";
    if (CalendarUtil.isHolidayEvent(mouseEnterInfo?.event)) {
      contentHTML = title;
    } else {
      const end = mouseEnterInfo?.event?.end;
      const calendarIds = Array.isArray(
        mouseEnterInfo?.event?.extendedProps?.calendarIds,
      )
        ? mouseEnterInfo?.event?.extendedProps?.calendarIds[0]
        : mouseEnterInfo?.event?.extendedProps?.calendarIds;
      const calendarObjects = calendars as unknown as CalendarViewObject[];
      const calendarFind = Array.isArray(calendarObjects)
        ? calendarObjects.find((x) => x.id === calendarIds)
        : null;
      if (!calendarFind) {
        return;
      }
      const isSameDay =
        moment(start).format("YYYY-MM-DD") === moment(end).format("YYYY-MM-DD");
      const dateStr = isSameDay
        ? `${moment(start).format("HH:mm")}-${moment(end).format("HH:mm")}`
        : `${moment(start).format("DD MMM (HH:mm)")} - ${moment(end).format("DD MMM (HH:mm)")}`;
      contentHTML = `
        <div>
            <div>
                <span style="
                display:inline-block;
                background: ${calendarFind?.color}; 
                border:1px solid #eee; 
                width:10px; height:10px
                "></span>
                <div style="display:inline-block">${calendarFind?.name}</div>
            </div>
            <div>${title}</div>
            <div>${dateStr}</div>
        </div>
      `;
    }
    tippyInstance = tippy([mouseEnterInfo?.el], {
      content: contentHTML,
      allowHTML: true,
      followCursor: true,
      plugins: [followCursor],
    });
  };

  const eventMouseLeave = () => {
    if (tippyInstance) {
      if (tippyInstance && tippyInstance.length > 0) {
        for (const instance of tippyInstance) {
          instance.destroy();
        }
      }
    }
  };

  const eventClick = (clickInfo: EventClickArg) => {
    if (!CalendarUtil.isHolidayEvent(clickInfo?.event)) {
      setDayInfo(clickInfo);
      setEventModalMode("edit");
    } else {
      setDayInfo(clickInfo);
      setEventModalMode("add");
    }
    setShowEventModal(true);
  };

  const dateSelect = (dateSelectInfo: DateSelectArg) => {
    console.log("dateSelectInfo", dateSelectInfo);
    const singleDay =
      moment(dateSelectInfo.start).format("YYYY-MM-DD") ===
        moment(dateSelectInfo.end).format("YYYY-MM-DD") ||
      moment(dateSelectInfo.end).diff(moment(dateSelectInfo.start), "days") ===
        1;
    setDayInfo(dateSelectInfo);
    if (singleDay) {
      setEventModalMode("add");
      setShowEventModal(true);
      setSelectedCount(1);
    } else {
      setSelectedCount(
        moment(dateSelectInfo.end).diff(moment(dateSelectInfo.start), "days"),
      );
    }
  };

  const renderEventContent = (contentArg: EventContentArg) => {
    const deleteButtonClick = (event: MouseEvent, arg: EventContentArg) => {
      event.stopPropagation();
      setDayInfo(arg);
      setShowDeleteModal(true);
    };
    // EventContentArg
    return CalendarEventRender.EventRender(
      contentArg,
      calendarOptions,
      selectedCalendars,
      deleteButtonClick,
    );
  };

  const createMultiDayEvent = () => {
    setEventModalMode("add");
    setShowEventModal(true);
  };

  if (loaded) {
    return (
      <>
        <IconButtonGroup
          toast={toast}
          container_offsetLeft={container_offsetLeft}
          calendarOptions={calendarOptions}
          selectedCalendars={selectedCalendars}
          setSelectedCalendars={setSelectedCalendars}
          onPlaceClick={() => {
            setShowPlaceModal(true);
          }}
          onCalendarClick={() => {
            setShowCalendarModal(true);
          }}
        />

        <FullCalendar
          locale={CALENDAR_LOCALE_MAP[locale] ?? "en"}
          ref={calendarRef}
          selectable
          themeSystem="bootstrap5"
          contentHeight="78vh"
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            listPlugin,
            interactionPlugin,
            bootstrap5Plugin,
          ]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next",
            center: "title",
            right: "", //"dayGridMonth,timeGridWeek,timeGridDay", // user can switch between the two
          }}
          datesSet={datesSet}
          // dateClick={dateClick}
          select={dateSelect}
          unselect={(e) => {
            setSelectedCount(0);
          }}
          events={calendarEventArray}
          eventContent={renderEventContent}
          eventClick={eventClick}
          eventMouseEnter={eventMouseEnter}
          eventMouseLeave={eventMouseLeave}
        />

        <button
          style={{
            height: "60px",
            width: "60px",
            borderRadius: "5px",
            backgroundColor: "#d3d3d3",
            opacity: 0.5,
            position: "absolute",
            bottom: "20px",
            right: "20px",
            textAlign: "center",
            lineHeight: "60px",
            fontSize: "24px",
            fontWeight: "bold",
            cursor: "pointer",
            border: "none",
            display: selectedCount > 1 ? "block" : "none",
          }}
          onClick={createMultiDayEvent}
        >
          {selectedCount}
        </button>

        <EventModal
          toast={toast}
          mode={eventModalMode}
          show={showEventModal}
          data={dayInfo}
          onSaveBtnClick={onSaveBtnClick}
          onClose={onHideEventModal}
          isCalendar={true}
          calendars={
            (calendars as unknown as CalendarViewObject[]) ?? undefined
          }
          places={(places as unknown as cal_place[]) ?? undefined}
        />
        <DeleteEventModal
          show={showDeleteModal}
          data={dayInfo}
          onConfirmBtnClick={onDeleteBtnClick}
          onClose={onHideDeleteModal}
          calendars={
            (calendars as unknown as CalendarViewObject[]) ?? undefined
          }
        />
        <CalendarListModal
          show={showCalendarModal}
          onClose={() => setShowCalendarModal(false)}
          toast={toast}
        />
        <PlaceListModal
          show={showPlaceModal}
          onClose={() => setShowPlaceModal(false)}
          toast={toast}
        />
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            className: "",
            style: common.toasterStyle,
          }}
        />
      </>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        height: "80vh",
      }}
    >
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );
}
